







import {Component, Prop, Vue} from "vue-property-decorator";
import {all, create} from "mathjs";
import PxDropdown from "@/views/components/input/formFields/PxDropdown.vue";
import PxNumberInput from "@/views/components/input/formFields/PxNumberInput.vue";
import ProbabilisticChart from "@/modules/lcca/views/components/ProbablisticChart.vue";
import PxTextArea from "@/views/components/input/formFields/PxTextArea.vue";
import PxTextEntryBox from "@/views/components/input/formFields/PxTextEntryBox.vue";
import QuickHelp from "@/views/components/input/QuickHelp.vue";
import PrVehicleDistributionPlot from "@/modules/perRoad/views/components/traffic/PrVehicleDistributionPlot.vue";
import {
  IPrTireLoadImperial,
  IPrTireLoadMetric,
  IWesleaConfig,
  IWesleaResult
} from "@/modules/perRoad/interfaces/monteCarlo";
import {UnitSystem} from "@/interfaces/units";
import {getContactPatchRadius} from "@/modules/structure/lib/calculateContactPatchRadius";

import {formatStrainResult} from "@/lib/util/numberFormatters";

const mathjsConfig = {};
const math = create(all, mathjsConfig);


const echarts = require("echarts");

@Component({
  components: {
    PrVehicleDistributionPlot,
    QuickHelp,
    PxTextEntryBox,
    PxTextArea,
    ProbabilisticChart,
    PxDropdown,
    PxNumberInput,
  },
})
export default class CriticalLocationLayoutInspector extends Vue {

  @Prop() id!: string;

  @Prop() loads!: IPrTireLoadImperial[] | IPrTireLoadMetric[];

  @Prop() wesleaConfig!: IWesleaConfig;

  @Prop() wesleaResults!: IWesleaResult[];

  @Prop({default: 1}) layerToShow!: number;

  @Prop({default: 0}) externalPlotKey!: number;


  get plotId() {
    return `${this.id}-critical-layout`;
  }

  /**
   * Configure the results display
   */
  visualMapDimension = 5;
  visualMapMax = 100;
  visualMapMin = 0;
  visualMapDimensionOptions = [
    {value: 2, text: "Normal Strain - X"},
    {value: 3, text: "Normal Strain - Y"},
    {value: 5, text: "Normal Strain - Max of X & Y"},
    {value: 4, text: "Normal Strain - Z"},
    {value: 6, text: "Deflection - Z"},
  ];

  formatStrainResult = formatStrainResult;

  // 295/75r22.5
  tireWidth_in = 11.6;
  tireWidth_cm = this.tireWidth_in * 2.54;
  tireDiameter_in = 40;
  tireDiameter_cm = this.tireDiameter_in * 2.54;

  get tireWidth() {
    return this.isImperial ? this.tireWidth_in : this.tireWidth_cm;
  }

  get isImperial() {
    return this.loads[0].unitSystem === UnitSystem.Imperial;
  }

  internalPlotKey = 0;

  updatePlotKey() {
    this.internalPlotKey += 1;
  }

  get plotKey() {
    return this.internalPlotKey + this.externalPlotKey;
  }

  get divHeight() {
    // if (this.axleType === PrAxleConfigurationType.TRIDEM) {
    //   return "900px";
    // } else if (this.axleType === PrAxleConfigurationType.TANDEM) {
    //   return "600px";
    // }
    return "600px";
  }

  /**
   * Chart formatting
   */

  get chartYAxisLimits() {
    let yMax = 0 - this.tireDiameter_in / 2;
    const yMin = 0 - this.tireDiameter_in / 2;
    yMax = Math.max(...this.tireLocationSeries[0].data.map((c: number[]) => c[1])) + this.tireDiameter_in / 2;

    // Pad left and right of chart by amt
    const padding = 0.2;
    const width = yMax - yMin;
    const minPad = -(width * padding) / 2;
    const maxPad = (width * padding) / 2;

    return [yMin + minPad, yMax + maxPad];

  }

  get chartXAxisLimits() {
    /**
     * Calculate content-aware limits for x axis: based on y-axis height to get equal pitch on both axes.
     */
    let width = 600;
    let height = 400;

    const el = document.getElementById(this.plotId);
    if (el) {
      // console.log("Creating width from el");
      width = el.clientWidth > 0 ? el.clientWidth : width;
      height = el.clientHeight > 0 ? el.clientHeight : height;
      // console.log(width, height);
    }

    const displayRatio = width / height;

    const xMin = 0 - this.tireWidth;
    const xMax = Math.max(...this.tireLocationSeries[0].data.map((c: number[]) => c[0])) + this.tireWidth;

    const xAxisWidthFromYAxis = this.chartYAxisLimits[1] - this.chartYAxisLimits[0];
    let xAxisWidth = xAxisWidthFromYAxis * displayRatio;

    // Get center of wheel path drawing
    const xAxisCenter = xMin + (xMax - xMin) / 2;

    // Find left and right limits
    return [xAxisCenter - xAxisWidth / 2, xAxisCenter + xAxisWidth / 2];
  }

  /**
   * Data series
   */

  get tireLocationSeries() {
    return [{
      type: "scatter",
      //@ts-ignore ts doesn't like the load types
      data: this.loads.map((load, i) => {


        const xy = load.unitSystem === UnitSystem.Imperial ? load.location_in : load.location_cm;
        const pressure = load.unitSystem === UnitSystem.Imperial ? load.tirePressure_psi : load.tirePressure_mpa;

        return [xy.x, xy.y, i, pressure];
      })
    }];
  }

  get criticalLocationSeries(): any[] {

    // console.log("Getting critical locations");

    if (!this.wesleaConfig || this.wesleaConfig.criticallocations.length === 0) {
      // console.log("No critical locations found");
      return [];
    }

    const series: any [] = [{
      type: "scatter",
      name: "Critical Location",
      z2: 0,
      symbol: "diamond",
      symbolSize: 10,
      tooltip: {
        formatter: (params: any, ticket: any, callback: any) => {
          const ns = params.data.normal_strain;
          const strainString = `Normal Strain<br>x: ${(ns.x).toFixed(0)} µS <br> y: ${(ns.y).toFixed(0)} µS<br><br>xloc: ${params.data.loc.x}<br>yloc: ${params.data.loc.y}<br>zloc: ${params.data.loc.z}`;

          return `<strong>${params.seriesName} ${params.data.name}</strong><br>${strainString}`;
        }
      },
      data: []
    }];

    this.visualMapMin = 1000;
    this.visualMapMax = -1000;

    this.wesleaConfig.criticallocations.forEach((c, j) => {
      if (c.l === this.layerToShow) {
        // Normal strain vars
        let nsx = 0;
        let nsy = 0;
        let nsz = 0;
        let dz = 0;

        let r: IWesleaResult | null  = null;
        try {
          r = this.wesleaResults.filter(r => r.id === c.i)[0];
        } catch (e){
          (() => {})(); //noop
        }

        if (!(r === null)) {
          const normalResult = r.normal_strain;
          nsx = normalResult.x;
          nsy = normalResult.y;
          nsz = normalResult.z;
          dz = r.deflection.z;
        }


        const v = Math.min(nsx, nsy);

        switch (this.visualMapDimension) {
          case 2:
            this.visualMapMin = nsx < this.visualMapMin ? nsx : this.visualMapMin;
            this.visualMapMax = nsx > this.visualMapMax ? nsx : this.visualMapMax;
            break;
          case 3:
            this.visualMapMin = nsy < this.visualMapMin ? nsy : this.visualMapMin;
            this.visualMapMax = nsy > this.visualMapMax ? nsy : this.visualMapMax;
            break;
          case 4:
            this.visualMapMin = nsz < this.visualMapMin ? nsz : this.visualMapMin;
            this.visualMapMax = nsz > this.visualMapMax ? nsz : this.visualMapMax;
            break;
          case 5:
            this.visualMapMin = v < this.visualMapMin ? v : this.visualMapMin;
            this.visualMapMax = v > this.visualMapMax ? v : this.visualMapMax;
            break;
          case 6:
            this.visualMapMin = dz < this.visualMapMin ? dz : this.visualMapMin;
            this.visualMapMax = dz > this.visualMapMax ? dz : this.visualMapMax;
            break;
        }


        series[0].data.push({
          value: [c.x, c.y, nsx, nsy, nsz, Math.max(nsx, nsy)],
          // name: `${j}-${c.l}`,
          name: `${c.i}`,
          loc: {
            x: c.x,
            y: c.y,
            z: c.z
          },
          normal_strain: {
            x: nsx * 1000000,
            y: nsy * 1000000,
            z: nsz * 1000000
          }
        });
      }
    });
    return series;
  }

  get tireOutlineSeries(): any[] {
    const series: any[] = [];

    const tireWidth_in = this.tireWidth_in;
    const tireDiameter_in = this.tireDiameter_in;

    series.push({
      type: "custom",
      name: "Tire",
      data: this.tireLocationSeries[0].data,
      tooltip: {
        formatter: (params: any, ticket: any, callback: any) => {
          return `<strong>${params.seriesName} ${params.data[2]}</strong><br>Pressure: ${params.data[3]} psi`;
        },
        textStyle: {}
      },
      renderItem: function (params: any, api: any) {
        const xLoc = api.value(0) - tireWidth_in / 2;
        const yLoc = api.value(1) + tireDiameter_in / 2;

        const start = api.coord([xLoc, yLoc]);
        const size = api.size([tireWidth_in, tireDiameter_in]);

        return {
          type: "rect",
          shape: {
            x: start[0],
            y: start[1],
            width: size[0],
            height: size[1]
          },
          style: {
            fill: "#555555"
          },
          textContent: {
            type: "text",
            style: {
              text: `Tire ${api.value(2)}`,
            }
          },
          textConfig: {
            position: "insideTop",
          }
        };
      },

    });
    return series;
  }

  get contactPatchSeries() {
    const series: any[] = [];

    let weightUnits = "lbf";
    let lengthUnits = "in";

    // Signature should be IPrTireLoadImperial | IPrTireLoadMetric but ts keeps yelling at me for that
    //@ts-ignore
    const patchData = this.loads.map((load) => {

      const xy = load.unitSystem === UnitSystem.Imperial ? load.location_in : load.location_cm;
      const weight = load.unitSystem === UnitSystem.Imperial ? load.load_lbf : load.load_kg;
      const pressure = load.unitSystem === UnitSystem.Imperial ? load.tirePressure_psi : load.tirePressure_mpa;
      const radius = getContactPatchRadius(load);

      weightUnits = load.unitSystem === UnitSystem.Imperial ? "lbf" : "kg";
      lengthUnits = load.unitSystem === UnitSystem.Imperial ? "lbf" : "cm";

      return [xy.x, xy.y, weight, pressure, radius];
      // return [load.location.x, load.location.y, load.load, load.radius];
    });


    series.push({
      type: "custom",
      name: "Contact Patch",
      tooltip: {
        formatter: (params: any, ticket: any, callback: any) => {
          return `<strong>${params.seriesName}</strong><br>Radius: ${params.data[4]} ${lengthUnits}<br>Load: ${params.data[2]} ${weightUnits}`;
        }
      },
      data: patchData,
      renderItem: function (params: any, api: any) {

        const loc = api.coord([api.value(0), api.value(1)]);

        return {
          type: "circle",

          shape: {
            cx: loc[0],
            cy: loc[1],
            r: api.size([api.value(4), 0])[0],
          },
          z2: 2,
          textContent: {
            type: "text",
            style: {
              text: `${(api.value(2)).toFixed(0)} ${weightUnits}`,
              stroke: null,
              fill: "#AAAAAA"
            },
          },
          textConfig: {
            position: "top"
          },
          style: {
            fill: "#AAAAAA"
          },

        };
      },

    });

    return series;

  }

  /**
   *
   * Chart creation, updating
   *
   */

  chart: any = null;

  createPlot() {
    if (this.chart === null) {
      const el = document.getElementById(this.plotId);
      this.chart = echarts.init(el);
    } else {
      this.chart.dispose();
      this.chart = null;
      const el = document.getElementById(this.plotId);
      this.chart = echarts.init(el);
    }

    this.chart.setOption({
      grid: {
        top: 5,
        right: 5,
        bottom: 45,
        left: 45,
        show: true,
        borderWidth: 0,
        borderColor: "#000",
      },
      visualMap: {
        left: "right",
        top: "center",
        calculable: true,
        dimension: this.visualMapDimension,
        min: this.visualMapMin,
        max: this.visualMapMax,
        precision: 6,
        inRange: {
          color: ["#fde725", "#5ec962", "#21918c", "#3b528b", "#440154"].reverse()
        }
      },
      xAxis: {
        name: "Horizontal Distance (in)",
        nameLocation: "center",
        nameGap: 25,
        min: this.chartXAxisLimits[0],
        max: this.chartXAxisLimits[1],
      },
      yAxis: {
        name: "Longitudinal Distance (in)",
        nameLocation: "center",
        nameGap: 25,
        min: this.chartYAxisLimits[0],
        max: this.chartYAxisLimits[1]

      },
      series: [
        ...this.contactPatchSeries,
        ...this.tireOutlineSeries,
        ...this.criticalLocationSeries,
      ],
      tooltip: {
        trigger: "item"
      }
    });
  }

  mounted() {
    const a = this.criticalLocationSeries;
    this.createPlot();
  }

  updated() {
    const a = this.criticalLocationSeries;
    this.createPlot();
  }


}
